import { Component, Input, OnChanges } from '@angular/core';
import { ClosingDetailsFacade } from './closing-details.facade';
import { ClosingDetailsActionType, ClosingDetailsComponentData } from './closing-details.model';
import { SubmissionDetailsParams } from '../../submission-details.model';
import { SubmissionDetailsActionsService } from '../../actions/submission-details-actions.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DownloadRequirementDocumentData, DeleteRequirementDocumentData, SaveRequirementDocumentData } from '../../../../domain/models/submissions.model';
import { AppBarAction, AppPageService } from 'common';
import { finalize, tap } from 'rxjs/operators';
import { ClosingSubmissionAdapter } from './closing-details.data';

@UntilDestroy()
@Component({
    selector: 'ifbp-closing-details',
    templateUrl: './closing-details.component.html',
    styleUrls: ['./closing-details.component.scss'],
    providers: [ClosingDetailsFacade, ClosingSubmissionAdapter],
    standalone: false
})
export class ClosingDetailsComponent implements OnChanges {
  viewData: ClosingDetailsComponentData;
  isFirstInit: boolean = true;
  actionInProgress: boolean;

  @Input()
  params: SubmissionDetailsParams;

  constructor(
    private readonly facade: ClosingDetailsFacade,
    private readonly actions: SubmissionDetailsActionsService,
    private readonly appPageService: AppPageService
  ) { }

  ngOnChanges(): void {
    this.initData();
    this.initActions();
  }

  private initData(): void {
    this.facade.getComponentData$(this.params.appId).pipe(untilDestroyed(this))
      .subscribe(data => {
        this.viewData = data;
        this.actions.markClosingDocumentsSectionAsInvalid(false);
        if (this.isFirstInit) {
          this.firstInitActions(data);
          this.isFirstInit = false;
        }
      });
  }

  private firstInitActions(data: ClosingDetailsComponentData): void {
    this.facade.openIncompleteSubmissionDialogIfNeeded(data.application);
    this.actions.markDataAsLoaded(true);
  }

  private initActions(): void {
    this.actions.deleteRequirementDocument$.pipe(untilDestroyed(this))
      .subscribe((data: DeleteRequirementDocumentData) => {
        this.facade.removeCheckoutRequirementDocument(<number>this.viewData.selectedOfferBundleId, data);
      })

    this.actions.downloadRequirementDocument$.pipe(untilDestroyed(this))
      .subscribe((data: DownloadRequirementDocumentData) => {
        this.facade.downloadCheckoutRequirementDocument(data);
      })

    this.actions.saveRequirementDocuments$.pipe(untilDestroyed(this))
      .subscribe((data: SaveRequirementDocumentData[]) => {
        this.facade.saveCheckoutRequirementDocuments(<number>this.viewData.selectedOfferBundleId, data)
      })
  }

  onAppBarActionInvoke(action: AppBarAction, appId: number): void {
    if (this.actionInProgress) {
      return;
    }
    if (action.id !== ClosingDetailsActionType.SubmitForReview) {
      return;
    }

    this.submitForReview(appId);
  }

  private submitForReview(appId: number): void {
    if (!this.viewData.offerRequirementsData?.isRequirementsDataValid) {
      this.actions.markClosingDocumentsSectionAsInvalid(true);
      return;
    }

    this.actionInProgress = true;
    this.facade.submitForReview(appId, this.viewData.advisorData)
      .pipe(
        tap(() => this.handleSuccess()),
        finalize(() => this.handleComplete()))
      .subscribe();
  }

  private handleSuccess(): void {
    this.appPageService.back();
  }

  private handleComplete(): void {
    this.actionInProgress = false;
  }
}
